// Beam Report PDF Generator
// Generates comprehensive PDF reports for beam calculations

import { jsPDF, defaultAutoTableConfig, safeToString } from './pdfConfig';
import { BeamCalculationResult, BeamProfile } from './BeamCalculator';
import { MaterialProperties } from './MaterialDatabase';
import { convertPolishChars } from '../../utils/pdfFonts';

export class BeamReportGenerator {
  private doc: jsPDF;
  private currentY: number = 20;
  private pageNumber: number = 1;
  private readonly pageHeight: number = 297;
  private readonly marginBottom: number = 20;
  
  constructor() {
    // Constructor only sets initial values
    // Font registration happens in generateReport (async)
    this.currentY = 20;
    this.pageNumber = 1;
    this.pageHeight = 297;
    this.marginBottom = 20;
  }
  
  private checkPageBreak(requiredSpace: number = 30): void {
    if (this.currentY + requiredSpace > this.pageHeight - this.marginBottom) {
      this.addNewPage();
    }
  }
  
  private addNewPage(): void {
    this.doc.addPage();
    this.pageNumber++;
    this.currentY = 20;
    this.addHeader();
  }
  
  private addHeader(): void {
    // Logo/Tytuł
    this.doc.setFontSize(18);
    this.doc.setTextColor(255, 102, 0);
    this.doc.text('PlexiSystem', 20, this.currentY);
    
    this.doc.setFontSize(10);
    this.doc.setTextColor(100, 100, 100);
    this.doc.text('Kalkulator Wytrzymalosci Belek v2.0', 120, this.currentY);
    
    // Linia pod nagłówkiem
    this.currentY += 5;
    this.doc.setDrawColor(255, 102, 0);
    this.doc.setLineWidth(0.5);
    this.doc.line(20, this.currentY, 190, this.currentY);
    
    this.currentY += 10;
  }
  
  private addFooter(): void {
    const footerY = this.pageHeight - 10;
    this.doc.setFontSize(8);
    this.doc.setTextColor(150, 150, 150);
    this.doc.text(`Strona ${this.pageNumber}`, 105, footerY, { align: 'center' });
    this.doc.text(`Raport wygenerowany: ${new Date().toLocaleDateString('pl-PL')}`, 20, footerY);
  }
  
  private drawBeamDiagram(
    profile: BeamProfile,
    length: number,
    supports: any[],
    loads: any[],
    y: number
  ): number {
    const diagramWidth = 170;
    const diagramHeight = 60;
    const startX = 20;
    
    // Tło diagramu
    this.doc.setFillColor(245, 245, 245);
    this.doc.rect(startX, y, diagramWidth, diagramHeight, 'F');
    
    // Belka
    const beamY = y + diagramHeight / 2;
    const beamStartX = startX + 15;
    const beamEndX = startX + diagramWidth - 15;
    const beamLength = beamEndX - beamStartX;
    
    // Rysuj belkę w zależności od typu profilu
    this.doc.setDrawColor(50, 50, 50);
    this.doc.setLineWidth(2);
    
    if (profile.type === 'rod') {
      // Pręt - linia ciągła
      this.doc.line(beamStartX, beamY, beamEndX, beamY);
      
      // Średnica
      this.doc.setFontSize(8);
      this.doc.text(`Ø${profile.diameter}mm`, beamStartX, beamY - 5);
      
    } else if (profile.type === 'tube') {
      // Rurka - dwie linie
      this.doc.line(beamStartX, beamY - 2, beamEndX, beamY - 2);
      this.doc.line(beamStartX, beamY + 2, beamEndX, beamY + 2);
      
      // Wymiary
      this.doc.setFontSize(8);
      this.doc.text(`Ø${profile.outerDiameter}/${profile.innerDiameter}mm`, beamStartX, beamY - 7);
      
    } else if (profile.type === 'rectangular') {
      // Prostokąt
      this.doc.rect(beamStartX, beamY - 4, beamEndX - beamStartX, 8);
      
      // Wymiary
      this.doc.setFontSize(8);
      this.doc.text(`${profile.width}x${profile.height}mm`, beamStartX, beamY - 7);
    }
    
    // Podpory
    supports.forEach(support => {
      const supportX = beamStartX + (support.position / length) * beamLength;
      
      if (support.type === 'fixed') {
        // Utwierdzenie
        this.doc.setFillColor(100, 100, 100);
        this.doc.rect(supportX - 2, beamY + 5, 4, 8, 'F');
        // Kreskowanie
        for (let i = 0; i < 5; i++) {
          this.doc.line(
            supportX - 4 + i * 2,
            beamY + 13,
            supportX - 2 + i * 2,
            beamY + 15
          );
        }
      } else if (support.type === 'pinned') {
        // Podpora przegubowa
        this.doc.setFillColor(255, 255, 255);
        this.doc.circle(supportX, beamY + 8, 3);
        // Rysuj trójkąt ręcznie
        this.doc.lines([
          [8, 0],
          [-4, 5],
          [-4, -5]
        ], supportX - 4, beamY + 11, [1, 1], 'D');
      }
    });
    
    // Obciążenia
    loads.forEach(load => {
      this.doc.setDrawColor(255, 0, 0);
      this.doc.setLineWidth(1);
      
      if (load.type === 'uniform') {
        // Obciążenie równomierne
        const loadStartX = beamStartX + (load.startPos / length) * beamLength;
        const loadEndX = beamStartX + (load.endPos / length) * beamLength;
        
        for (let x = loadStartX; x <= loadEndX; x += 10) {
          this.drawArrow(x, beamY - 15, x, beamY - 3, 'red');
        }
        
        this.doc.setFontSize(8);
        this.doc.setTextColor(255, 0, 0);
        this.doc.text(`${load.value} N/m`, (loadStartX + loadEndX) / 2, beamY - 18, { align: 'center' });
        
      } else if (load.type === 'point') {
        // Obciążenie punktowe
        const loadX = beamStartX + (load.position / length) * beamLength;
        this.drawArrow(loadX, beamY - 20, loadX, beamY - 3, 'red');
        
        this.doc.setFontSize(8);
        this.doc.setTextColor(255, 0, 0);
        this.doc.text(`${load.value} N`, loadX, beamY - 23, { align: 'center' });
      }
    });
    
    // Wymiar długości
    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(0.5);
    this.doc.line(beamStartX, beamY + 25, beamEndX, beamY + 25);
    this.doc.line(beamStartX, beamY + 23, beamStartX, beamY + 27);
    this.doc.line(beamEndX, beamY + 23, beamEndX, beamY + 27);
    
    this.doc.setFontSize(9);
    this.doc.setTextColor(0, 0, 0);
    this.doc.text(`${length} mm`, (beamStartX + beamEndX) / 2, beamY + 30, { align: 'center' });
    
    return diagramHeight + 15;
  }
  
  private drawArrow(x1: number, y1: number, x2: number, y2: number, color: string): void {
    const headLength = 3;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    
    // Linia główna
    this.doc.line(x1, y1, x2, y2);
    
    // Grot strzałki
    this.doc.line(
      x2,
      y2,
      x2 - headLength * Math.cos(angle - Math.PI / 6),
      y2 - headLength * Math.sin(angle - Math.PI / 6)
    );
    this.doc.line(
      x2,
      y2,
      x2 - headLength * Math.cos(angle + Math.PI / 6),
      y2 - headLength * Math.sin(angle + Math.PI / 6)
    );
  }
  
  private drawDeflectionDiagram(
    deflectionData: number[],
    maxDeflection: number,
    maxPosition: number,
    length: number,
    y: number
  ): number {
    const diagramWidth = 170;
    const diagramHeight = 50;
    const startX = 20;
    
    // Tło
    this.doc.setFillColor(250, 250, 250);
    this.doc.rect(startX, y, diagramWidth, diagramHeight, 'F');
    
    // Osie
    this.doc.setDrawColor(100, 100, 100);
    this.doc.setLineWidth(0.5);
    this.doc.line(startX + 10, y + diagramHeight / 2, startX + diagramWidth - 10, y + diagramHeight / 2);
    
    // Krzywa ugięcia
    this.doc.setDrawColor(0, 100, 200);
    this.doc.setLineWidth(1.5);
    
    if (deflectionData && deflectionData.length > 0) {
      const scaleX = (diagramWidth - 20) / deflectionData.length;
      const scaleY = maxDeflection !== 0 ? (diagramHeight - 10) / (2 * Math.abs(maxDeflection)) : 1;
      
      for (let i = 0; i < deflectionData.length - 1; i++) {
        const x1 = startX + 10 + i * scaleX;
        const x2 = startX + 10 + (i + 1) * scaleX;
        const y1 = y + diagramHeight / 2 + (deflectionData[i] || 0) * scaleY;
        const y2 = y + diagramHeight / 2 + (deflectionData[i + 1] || 0) * scaleY;
        
        // Sprawdź czy wartości są prawidłowe
        if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
          this.doc.line(x1, y1, x2, y2);
        }
      }
      
      // Punkt maksymalnego ugięcia
      if (maxDeflection && maxPosition && length) {
        const maxX = startX + 10 + (maxPosition / length) * (diagramWidth - 20);
        const maxY = y + diagramHeight / 2 + maxDeflection * scaleY;
        
        if (!isNaN(maxX) && !isNaN(maxY)) {
          this.doc.setFillColor(255, 0, 0);
          this.doc.circle(maxX, maxY, 2, 'F');
          
          this.doc.setFontSize(8);
          this.doc.setTextColor(255, 0, 0);
          this.doc.text(`Max: ${maxDeflection.toFixed(2)} mm`, maxX + 5, maxY);
        }
      }
    }
    
    // Etykieta
    this.doc.setFontSize(9);
    this.doc.setTextColor(0, 0, 0);
    this.doc.text('Wykres ugiecia', startX, y - 2);
    
    return diagramHeight + 10;
  }
  
  public generateReport(
    input: any,
    result: BeamCalculationResult,
    material: MaterialProperties
  ): void {
    // Create new document
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    this.debugLog('Starting beam report generation');
    // Strona tytułowa
    this.addHeader();
    
    // Tytuł raportu
    this.doc.setFontSize(24);
    this.doc.setTextColor(0, 0, 0);
    this.doc.text('RAPORT ANALIZY BELKI', 105, this.currentY + 20, { align: 'center' });
    
    this.currentY += 35;
    
    // Informacje podstawowe
    this.doc.setFontSize(12);
    this.doc.setTextColor(60, 60, 60);
    
    const projectInfo = [
      ['Nazwa projektu:', safeToString(input?.projectName, 'Belka PMMA')],
      ['Klient:', safeToString(input?.client, 'Klient')],
      ['Data analizy:', new Date().toLocaleDateString('pl-PL')],
      ['Godzina:', new Date().toLocaleTimeString('pl-PL')],
      ['Material:', safeToString(material?.displayName, 'Nieznany')],
      ['Profil:', safeToString(input?.profile?.name, '-')],
      ['Dlugosc:', `${safeToString(input?.length, '0')} mm`],
      ['Obciazenie:', input?.loads?.length > 0 ? 
        `${safeToString(input.loads[0].value)} ${input.loads[0].type === 'uniform' ? 'N/m' : 'N'}` : '-']
    ];
    
    projectInfo.forEach(([label, value]) => {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(label, 40, this.currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(value, 90, this.currentY);
      this.currentY += 8;
    });
    
    // Status
    this.currentY += 10;
    this.doc.setFontSize(16);
    if (result.isSafe) {
      this.doc.setFillColor(0, 200, 0);
      this.doc.setTextColor(0, 150, 0);
      this.doc.text('KONSTRUKCJA SPELNIA WYMAGANIA', 105, this.currentY, { align: 'center' });
    } else {
      this.doc.setFillColor(255, 0, 0);
      this.doc.setTextColor(200, 0, 0);
      this.doc.text('KONSTRUKCJA NIE SPELNIA WYMAGAN', 105, this.currentY, { align: 'center' });
    }
    
    // Kluczowe parametry
    this.currentY += 15;
    this.doc.setFontSize(14);
    this.doc.setTextColor(0, 0, 0);
    this.doc.text('KLUCZOWE PARAMETRY:', 20, this.currentY);
    
    this.currentY += 8;
    this.doc.setFontSize(11);
    
    const keyParams = [
      `- Maksymalne ugiecie: ${result.maxDeflection?.toFixed(2) || '0.00'} mm (limit: ${result.allowableDeflection?.toFixed(2) || 'N/A'} mm)`,
      `- Maksymalne naprezenie: ${result.maxBendingStress?.toFixed(1) || '0.0'} MPa (limit: ${result.allowableStress?.toFixed(1) || 'N/A'} MPa)`,
      `- Wykorzystanie nosnosci: ${result.utilizationRatio?.toFixed(0) || '0'}%`,
      `- Margines bezpieczenstwa: ${result.utilizationRatio ? ((1 - result.utilizationRatio / 100) * 100).toFixed(0) : '0'}%`
    ];
    
    keyParams.forEach(param => {
      this.doc.text(param, 25, this.currentY);
      this.currentY += 7;
    });
    
    this.addFooter();
    
    // Strona 2: Schemat belki
    this.addNewPage();
    
    this.doc.setFontSize(16);
    this.doc.setTextColor(0, 0, 0);
    this.doc.text('SCHEMAT KONSTRUKCJI', 20, this.currentY);
    this.currentY += 10;
    
    // Diagram belki
    const diagramHeight = this.drawBeamDiagram(
      input.profile,
      input.length,
      input.supports,
      input.loads,
      this.currentY
    );
    this.currentY += diagramHeight;
    
    // Wykres ugięcia
    this.doc.setFontSize(16);
    this.doc.text('ANALIZA UGIECIA', 20, this.currentY);
    this.currentY += 10;
    
    const deflectionHeight = this.drawDeflectionDiagram(
      result.deflectionCurve || [],
      result.maxDeflection,
      result.maxDeflectionPosition,
      input.length,
      this.currentY
    );
    this.currentY += deflectionHeight;
    
    this.addFooter();
    
    // Strona 3: Szczegółowe wyniki
    this.addNewPage();
    
    this.doc.setFontSize(16);
    this.doc.text('SZCZEGOLOWA ANALIZA', 20, this.currentY);
    this.currentY += 10;
    
    // Tabela wyników
    const resultsData = [
      ['Parametr', 'Wartosc obliczona', 'Wartosc dopuszczalna', 'Status'],
      ['Ugiecie maksymalne', `${result.maxDeflection?.toFixed(2) || '0.00'} mm`, `${result.allowableDeflection?.toFixed(2) || 'N/A'} mm`, result.maxDeflection && result.allowableDeflection && result.maxDeflection <= result.allowableDeflection ? 'OK' : 'FAIL'],
      ['Naprezenie zginajace', `${result.maxBendingStress?.toFixed(1) || '0.0'} MPa`, `${result.allowableStress?.toFixed(1) || 'N/A'} MPa`, result.maxBendingStress && result.allowableStress && result.maxBendingStress <= result.allowableStress ? 'OK' : 'FAIL'],
      ['Naprezenie von Mises', `${result.vonMisesStress?.toFixed(1) || '0.0'} MPa`, `${material.yieldStrength || 'N/A'} MPa`, result.vonMisesStress && material.yieldStrength && result.vonMisesStress <= material.yieldStrength ? 'OK' : 'FAIL'],
      ['Wspolczynnik wykorzystania', `${result.utilizationRatio?.toFixed(0) || '0'}%`, '100%', result.utilizationRatio && result.utilizationRatio <= 100 ? 'OK' : 'FAIL']
    ];
    
    if (result.bucklingFactor) {
      resultsData.push([
        'Wspolczynnik wyboczenia',
        result.bucklingFactor?.toFixed(2) || 'N/A',
        '> 1.0',
        result.bucklingFactor && result.bucklingFactor > 1 ? 'OK' : 'FAIL'
      ]);
    }
    
    this.doc.autoTable({
      startY: this.currentY,
      head: [resultsData[0]],
      body: resultsData.slice(1),
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 5
      },
      headStyles: {
        fillColor: [255, 102, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        3: { halign: 'center' }
      }
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
    
    // Rekomendacje
    if (result.recommendations && result.recommendations.length > 0) {
      this.doc.setFontSize(14);
      this.doc.text('REKOMENDACJE:', 20, this.currentY);
      this.currentY += 8;
      
      this.doc.setFontSize(10);
      result.recommendations.forEach(rec => {
        this.doc.text(`- ${rec}`, 25, this.currentY);
        this.currentY += 6;
      });
    }
    
    this.addFooter();
    
    // Strona 4: Dane techniczne
    this.addNewPage();
    
    this.doc.setFontSize(16);
    this.doc.text('DANE TECHNICZNE MATERIALU', 20, this.currentY);
    this.currentY += 10;
    
    const materialData = [
      ['Parametr', 'Wartosc'],
      ['Nazwa handlowa', safeToString(material?.displayName)],
      ['Gestosc', `${safeToString(material?.density)} kg/m3`],
      ['Modul Younga', `${safeToString(material?.modulusElasticity)} MPa`],
      ['Wytrzymalosc na rozciaganie', `${safeToString(material?.tensileStrength)} MPa`],
      ['Wytrzymalosc na zginanie', `${safeToString(material?.flexuralStrength)} MPa`],
      ['Wytrzymalosc na sciskanie', `${safeToString(material?.compressiveStrength)} MPa`],
      ['Wspolczynnik Poissona', safeToString(material?.poissonRatio)],
      ['Max temperatura pracy', `${safeToString(material?.maxTemp)} C`],
      ['Odpornosc UV', material?.uvResistance ? 'TAK' : 'NIE'],
      ['Odpornosc chemiczna', safeToString(material?.chemicalResistance, 'SREDNIA')]
    ];
    
    this.doc.autoTable({
      startY: this.currentY,
      head: [materialData[0]],
      body: materialData.slice(1),
      theme: 'striped',
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [100, 100, 100],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      }
    });
    
    this.addFooter();
  }
  
  public save(filename: string = 'raport_belka.pdf'): void {
    this.doc.save(filename);
  }
  
  /**
   * Debug logging
   */
  private debugLog(message: string): void {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.log(`[PDF Beam] ${message}`);
    }
  }
}

export const beamReportGenerator = new BeamReportGenerator();
